import { Robot, UserPlus, Users } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { type AdminUser, usersQuery } from "@/components/admin/api";
import { BotDialog } from "@/components/admin/BotDialog";
import { InviteDialog } from "@/components/admin/InviteDialog";
import { LinkDialog, type LinkResult } from "@/components/admin/LinkDialog";
import { drawerSearch, QueryBoundary, roleName, UserCell, useDrawerParam } from "@/components/admin/shared";
import { UserDrawer } from "@/components/admin/UserDrawer";
import { useBreadcrumbs } from "@/components/shell/breadcrumbs";
import { Page } from "@/components/shell/Page";
import {
  Badge,
  Button,
  type DataColumn,
  DataTable,
  EmptyState,
  RelativeTime,
  SearchField,
  Select,
  type SortDescriptor,
  TableSkeleton,
} from "@/components/ui";
import { useMe } from "@/lib/queries";
import { toast } from "@/lib/toast";

export const Route = createFileRoute("/_app/admin/users")({
  validateSearch: drawerSearch("user"),
  component: UsersPage,
});

type Kind = "all" | "human" | "bot";
type Status = "all" | "active" | "disabled";

function UsersPage() {
  useBreadcrumbs([{ label: m.adm_title(), to: "/admin" }, { label: m.adm_nav_users() }]);
  const q = useQuery(usersQuery);
  const me = useMe().data;
  const [openId, setOpenId] = useDrawerParam("user");
  const [inviting, setInviting] = useState(false);
  const [botOpen, setBotOpen] = useState(false);
  const [link, setLink] = useState<LinkResult | null>(null);

  return (
    <Page
      title={m.adm_nav_users()}
      icon={Users}
      meta={m.adm_users_meta()}
      width="full"
      className="max-w-5xl"
      actions={
        <>
          <Button variant="secondary" onPress={() => setBotOpen(true)}>
            <Robot size={14} aria-hidden="true" />
            {m.adm_bot_title()}
          </Button>
          <Button variant="primary" onPress={() => setInviting(true)}>
            <UserPlus size={14} aria-hidden="true" />
            {m.adm_invite_title()}
          </Button>
        </>
      }
    >
      <QueryBoundary query={q} skeleton={<TableSkeleton rows={6} columns={4} />} needs="users.manage">
        {(users) => <UserTable users={users} onOpen={setOpenId} />}
      </QueryBoundary>

      <UserDrawer
        user={q.data?.find((u) => u.id === openId) ?? null}
        isSelf={openId === me?.id}
        meIsOwner={me?.isOwner === true}
        onClose={() => setOpenId(null)}
        onLink={setLink}
      />
      <InviteDialog
        isOpen={inviting}
        onOpenChange={setInviting}
        onCreated={(res) => {
          if (res.link) {
            setLink({
              title: m.adm_invite_link_title(),
              intro: m.adm_invite_link_intro({ email: res.invite.email }),
              link: res.link,
              description: m.adm_invite_link_hint(),
            });
          } else {
            toast.show({ title: m.adm_invite_sent({ email: res.invite.email }), tone: "success" });
          }
        }}
      />
      <BotDialog isOpen={botOpen} onOpenChange={setBotOpen} />
      <LinkDialog result={link} onClose={() => setLink(null)} />
    </Page>
  );
}

const collator = () => new Intl.Collator(undefined, { sensitivity: "base", numeric: true });

function UserTable({ users, onOpen }: { users: AdminUser[]; onOpen: (id: string) => void }) {
  const [query, setQuery] = useState("");
  const [kind, setKind] = useState<Kind>("all");
  const [status, setStatus] = useState<Status>("all");
  const [sort, setSort] = useState<SortDescriptor>({ column: "user", direction: "ascending" });
  const me = useMe().data;

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const cmp = collator();
    const dir = sort.direction === "descending" ? -1 : 1;
    return users
      .filter((u) => (kind === "all" ? true : u.kind === kind))
      .filter((u) => (status === "all" ? true : status === "disabled" ? Boolean(u.disabledAt) : !u.disabledAt))
      .filter((u) => !needle || u.name.toLowerCase().includes(needle) || u.email.toLowerCase().includes(needle))
      .sort((a, b) =>
        sort.column === "lastSeen"
          ? dir * ((a.lastSeenAt ? Date.parse(a.lastSeenAt) : 0) - (b.lastSeenAt ? Date.parse(b.lastSeenAt) : 0))
          : dir * cmp.compare(a.name, b.name),
      );
  }, [users, query, kind, status, sort]);

  const columns: DataColumn<AdminUser>[] = [
    {
      id: "user",
      header: m.adm_users_col_user(),
      isRowHeader: true,
      allowsSorting: true,
      className: "min-w-56",
      cell: (u) => (
        <UserCell
          name={u.name}
          email={u.kind === "bot" ? m.adm_bot() : u.email}
          seed={u.id}
          bot={u.kind === "bot"}
          disabled={Boolean(u.disabledAt)}
          you={u.id === me?.id}
        />
      ),
    },
    {
      id: "roles",
      header: m.adm_users_col_roles(),
      className: "min-w-40",
      cell: (u) => <RolesCell user={u} />,
    },
    {
      id: "twofactor",
      header: m.adm_users_col_2fa(),
      className: "hidden w-28 md:table-cell",
      cell: (u) =>
        u.kind === "bot" ? (
          <span className="text-fg-subtle">
            <span aria-hidden="true">-</span>
            <span className="sr-only">{m.adm_users_na()}</span>
          </span>
        ) : u.twoFactorEnabled ? (
          <Badge tone="success">{m.ui_on()}</Badge>
        ) : (
          <Badge>{m.ui_off()}</Badge>
        ),
    },
    {
      id: "lastSeen",
      header: m.adm_users_col_last_active(),
      allowsSorting: true,
      className: "hidden w-36 whitespace-nowrap sm:table-cell",
      cell: (u) =>
        u.lastSeenAt ? (
          <RelativeTime value={u.lastSeenAt} className="text-fg-muted" />
        ) : (
          <span className="text-fg-subtle">{m.ui_never()}</span>
        ),
    },
  ];

  const filtered = query.trim() !== "" || kind !== "all" || status !== "all";
  const clear = () => {
    setQuery("");
    setKind("all");
    setStatus("all");
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <SearchField
          label={m.adm_users_search()}
          placeholder={m.adm_users_search()}
          value={query}
          onChange={setQuery}
          className="w-full sm:w-72"
        />
        <Select
          label={m.adm_users_filter_kind()}
          labelHidden
          className="w-52"
          items={[
            { id: "all", label: m.adm_users_kind_all() },
            { id: "human", label: m.adm_users_kind_human() },
            { id: "bot", label: m.adm_users_kind_bot() },
          ]}
          selectedKey={kind}
          onSelectionChange={(k) => setKind(k as Kind)}
        />
        <Select
          label={m.adm_users_filter_status()}
          labelHidden
          className="w-52"
          items={[
            { id: "all", label: m.adm_users_status_all() },
            { id: "active", label: m.adm_status_active() },
            { id: "disabled", label: m.adm_status_disabled() },
          ]}
          selectedKey={status}
          onSelectionChange={(k) => setStatus(k as Status)}
        />
        <span className="ml-auto text-xs text-fg-muted tnum" aria-live="polite">
          {m.adm_users_count({ count: rows.length })}
        </span>
      </div>
      <DataTable
        label={m.adm_nav_users()}
        columns={columns}
        rows={rows}
        sortDescriptor={sort}
        onSortChange={setSort}
        onRowAction={(u) => onOpen(u.id)}
        renderEmptyState={() => (
          <EmptyState
            title={m.adm_users_none_title()}
            description={filtered ? m.adm_users_none_body() : m.adm_users_empty_body()}
            action={
              filtered ? (
                <Button variant="secondary" onPress={clear}>
                  {m.filter_clear()}
                </Button>
              ) : undefined
            }
          />
        )}
      />
    </div>
  );
}

/** Owner flag first, then the distinct role names across all grants, two shown and the rest counted. */
function RolesCell({ user }: { user: AdminUser }) {
  const names = [...new Map(user.roles.map((r) => [r.roleSlug, roleName({ name: r.roleName })])).values()];
  const shown = names.slice(0, user.isOwner ? 1 : 2);
  const rest = names.length - shown.length;
  if (!user.isOwner && names.length === 0) return <span className="text-fg-subtle">{m.adm_no_role()}</span>;
  return (
    <div className="flex flex-wrap items-center gap-1" title={names.join(", ")}>
      {user.isOwner ? <Badge tone="accent">{m.adm_owner()}</Badge> : null}
      {shown.map((n) => (
        <Badge key={n}>{n}</Badge>
      ))}
      {rest > 0 ? <span className="text-xs text-fg-subtle tnum">+{rest}</span> : null}
    </div>
  );
}
